<script lang="ts">
    import type {DirectoryInsight, DocumentAvailability, RecordHolding} from 'typings/portaro.be.types';
    import type {DocumentDetailReactivePageData, DocumentDetailStaticPageData, DocumentDetailView} from './types';
    import type ExemplarList from '../exemplar-list';
    import {getInjector} from 'core/svelte-context/context';
    import {KpDocumentDetailPageService} from './kp-document-detail-page.service';
    import {onMount} from 'svelte';
    import {createPageContext} from 'shared/layouts/page-context';
    import {ErpSidebarNavService} from '../../erp/components/erp-sidebar-nav/erp-sidebar-nav.service';
    import KpLayoutBuilder from 'shared/layouts/grid-system/built-layout/KpLayoutBuilder.svelte';
    import KpDocumentDetailHeadingPart from './parts/KpDocumentDetailHeadingPart.svelte';
    import KpDocumentDetailActionButtonsPart from './parts/KpDocumentDetailActionButtonsPart.svelte';
    import KpDocumentDetailSimilarDocumentsPart from './parts/KpDocumentDetailSimilarDocumentsPart.svelte';
    import KpDocumentDetailDetailsPart from './parts/KpDocumentDetailDetailsPart.svelte';
    import KpDocumentDetailTabsetPart from './parts/KpDocumentDetailTabsetPart.svelte';
    import KpDocumentDetailVolumesPart from './parts/KpDocumentDetailVolumesPart.svelte';
    import KpDocumentDetailExemplarsPart from './parts/KpDocumentDetailExemplarsPart.svelte';
    import KpDocumentDetailCommentsPart from './parts/KpDocumentDetailCommentsPart.svelte';
    import KpDocumentDetailNavigationButtonsPanelPart from './parts/KpDocumentDetailNavigationButtonsPanelPart.svelte';
    import KpDocumentDetailCustomParagraphPanelPart from './parts/KpDocumentDetailCustomParagraphPanelPart.svelte';
    import KpDocumentDetailAttachmentsPanelPart from './parts/KpDocumentDetailAttachmentsPanelPart.svelte';
    import KpDocumentDetailAdminPanelPart from './parts/KpDocumentDetailAdminPanelPart.svelte';
    import KpDocumentDetailEditPanelPart from './parts/KpDocumentDetailEditPanelPart.svelte';
    import KpDocumentDetailVisitedPanelPart from './parts/KpDocumentDetailVisitedPanelPart.svelte';
    import KpDocumentDetailFavouritesPanelPart from './parts/KpDocumentDetailFavouritesPanelPart.svelte';
    import KpDocumentDetailCustomImagePart from './parts/KpDocumentDetailCustomImagePart.svelte';
    import KpDocumentDetailSearchHistoryPart from './parts/KpDocumentDetailSearchHistoryPart.svelte';
    import KpRelatedRecordsTablePart from './parts/KpRelatedRecordsTablePart.svelte';
    import KpLayoutGridContainer from 'shared/layouts/grid-system/KpLayoutGridContainer.svelte';
    import EmptyPart from 'src/features/record/kp-document-detail-page/parts/EmptyPart.svelte';
    import {GenericRecordDetail} from 'src/features/erp-sutor/features/generic-record-detail';

    export let view: DocumentDetailView;

    const service = getInjector().getByToken<KpDocumentDetailPageService>(KpDocumentDetailPageService.serviceName);
    const erpSidebarNavService = getInjector().getByToken<ErpSidebarNavService>(ErpSidebarNavService.serviceName);

    const documentDetailComponents = {
        heading: KpDocumentDetailHeadingPart,
        details: KpDocumentDetailDetailsPart,
        tabset: KpDocumentDetailTabsetPart,
        volumes: KpDocumentDetailVolumesPart,
        exemplars: KpDocumentDetailExemplarsPart,
        comments: KpDocumentDetailCommentsPart,
        navigationButtonsPanel: KpDocumentDetailNavigationButtonsPanelPart,
        customParagraphPanel: KpDocumentDetailCustomParagraphPanelPart,
        attachmentsPanel: KpDocumentDetailAttachmentsPanelPart,
        editPanel: KpDocumentDetailEditPanelPart,
        adminPanel: KpDocumentDetailAdminPanelPart,
        favouritesPanel: KpDocumentDetailFavouritesPanelPart,
        visitedPanel: KpDocumentDetailVisitedPanelPart,
        similarDocuments: KpDocumentDetailSimilarDocumentsPart,
        actionButtons: KpDocumentDetailActionButtonsPart,
        customImage: KpDocumentDetailCustomImagePart,
        searchHistory: KpDocumentDetailSearchHistoryPart,
        relatedRecordsTable: KpRelatedRecordsTablePart,

        // Portaro ERP components - Imported from features/sutor directory
        sutorTabbedSubpages: GenericRecordDetail,
        sutorTopBar: EmptyPart // TODO: Remove from layout JSONs
    };

    let allRecordHoldings: RecordHolding[] | undefined;
    let visibleRecordHoldings: RecordHolding[] | undefined;
    let availability: DocumentAvailability | undefined;
    let directoryInsight: DirectoryInsight | undefined;
    let exemplarList: ExemplarList | undefined;

    const pageContext = createPageContext<DocumentDetailStaticPageData, DocumentDetailReactivePageData>(
        {model: view}, // Static
        {allRecordHoldings, visibleRecordHoldings, availability, directoryInsight, exemplarList} // Reactive
    );

    onMount(async () => {
        [allRecordHoldings, visibleRecordHoldings, availability, directoryInsight, exemplarList] = await Promise.all([
            service.getAllRecordHoldings(view.record),
            service.getVisibleRecordHoldings(view.record),
            service.getAvailability(view.record),
            service.getDirectoryInsight(view.record),
            service.getExemplarList(view.record, view.exemplarsView)
        ]);

        pageContext.updateReactiveData({allRecordHoldings, visibleRecordHoldings, availability, directoryInsight, exemplarList});

        // We want to collapse Sutor sidebar on "document detail" page
        erpSidebarNavService.toggleCollapse(true);
    });
</script>

<KpLayoutGridContainer additionalClasses="kp-document-detail-page" rowsWidth="{view.layout.settings.defaultRowWidth}">
    <KpLayoutBuilder layout="{view.layout}" supportedComponents="{documentDetailComponents}"/>
</KpLayoutGridContainer>

<style lang="less">
    :global {
        .kp-document-detail-page .side-column-panel:empty {
            display: none;
        }
    }
</style>